import {
  FlowHumanTaskCopilotDataContextObjectSchema,
  FlowHumanTaskDocumentTranscriptionResponsesSchema,
  FlowSubmitPriorAuthorizationBlockConfigSchema,
} from 'flow-schema';
import { z } from 'zod';

const OrderSpecificQuestionSchema = z.object({
  question: z.string(),
  code: z.string(),
  answer: z.string().nullable(),
  comment: z.string().optional().nullable(),
});

const DiagnosisSchema = z.object({
  description: z.string(),
  icd10Codes: z.array(z.string()).nullable(),
});

const PopulatedProcedureSchema = z.object({
  id: z.uuid(),
  patientOrderId: z.uuid(),
  standardProcedureId: z.string().nullable(),
  description: z.string(),
  cptCode: z.string().nullable(),
  areaOfInterest: z.string().nullable(),
  reasonForExam: z.string().nullable(),
  ehrReferenceValue: z.string().nullable(),
  rawDiagnoses: z.string().nullable(),
  diagnoses: z.array(DiagnosisSchema).nullable(),
});

const AuthorizationSubmissionSchema = z.object({
  id: z.uuid(),
  updated: z.string(),
  created: z.string(),
  patientOrderId: z.uuid(),
  payorPortal: z.string().nullable(),
  requestedDate: z.string().nullable(),
  obtainedDate: z.string().nullable(),
  effectiveDate: z.string().nullable(),
  expirationDate: z.string().nullable(),
  trackingNumber: z.string().nullable(),
  authorizationNumber: z.string().nullable(),
  notRequiredReason: z.string().nullable(),
  notRequiredComment: z.string().nullable(),
  exceptionReason: z.string().nullable(),
  exceptionComment: z.string().nullable(),
  rejectedReason: z.string().nullable(),
  rejectedComment: z.string().nullable(),
  pendingReason: z.string().nullable(),
  pendingComment: z.string().nullable(),
  verificationGsPath: z.string().nullable(),
  status: z.string(),
  rawStatus: z.string(),
  procedures: z.array(PopulatedProcedureSchema).nullable(),
});

export const BasePatientOrderSchema = z.object({
  id: z.uuid(),
  created: z.string(),
  updated: z.string(),
  patientId: z.string(),
  practiceId: z.string(),
  patientEhrId: z.string(),
  alternateReferredToEhrProviderId: z.string().nullable(),
  deletedTimestamp: z.string().nullable(),
  dischargeTime: z.string().nullable(),
  ehrCreated: z.string().nullable(),
  ehrInformationId: z.string(),
  ehrReferenceValue: z.string().nullable(),
  ehrScheduled: z.string().nullable(),
  ehrTimeSlotBookingId: z.string().nullable(),
  encounterId: z.string().nullable(),
  externalDocumentPacketId: z.string().nullable(),
  fileName: z.string().nullable(),
  fin: z.string().nullable(),
  followUpUnit: z.string().nullable(),
  followUpValue: z.number().nullable(),
  followUpVisitType: z.string().nullable(),
  isTest: z.boolean().nullable(),
  patientAddressId: z.string().nullable(),
  patientHospitalLocation: z.string().nullable(),
  patientType: z.string().nullable(),
  rawDiagnoses: z.string().nullable(),
  rawProcedures: z.string().nullable(),
  reasonForVisit: z.string().nullable(),
  referralComment: z.string().nullable(),
  referralCreated: z.string().nullable(),
  referralPriority: z.string().nullable(),
  referredByDepartment: z.string().nullable(),
  referredByEhrProviderId: z.string(),
  referredToDepartment: z.string().nullable(),
  referredToEhrProviderId: z.string().nullable(),
  registrationTime: z.string().nullable(),
  requestedAppointmentTimePeriodStart: z.string().optional().nullable(),
  requestedAppointmentTimePeriodEnd: z.string().optional().nullable(),
  requestListStatus: z.string().nullable(),
  scheduleLimitDate: z.string().nullable(),
  seriesAppointmentCount: z.number().nullable(),
  specialty: z.string().nullable(),
  status: z.string().optional().nullable(),
  payer: z.string().nullable(),
  authRequestedDate: z.string().nullable(),
  authObtainedDate: z.string().nullable(),
  authEffectiveDate: z.string().nullable(),
  authExpirationDate: z.string().nullable(),
  reasonForExam: z.string().nullable(),
  trackingNumber: z.string().nullable(),
  payerAuthorizationNumber: z.string().nullable(),
  ehrOrderingEncounterReferenceValue: z.string().nullable(),
});

export const PatientOrderPopulatedSchema = BasePatientOrderSchema.extend({
  authorizationStatus: z.string().nullable(),
  authorizationNote: z.string().nullable(),
  authorizationExceptionReason: z.string().nullable(),
  authorizationExceptionComment: z.string().nullable(),
  authorizationNotRequiredReason: z.string().nullable(),
  authorizationNotRequiredComment: z.string().nullable(),
  authorizationRejectedReason: z.string().nullable(),
  authorizationRejectedComment: z.string().nullable(),
  verificationGsPath: z.string().nullable(),
  diagnoses: z.array(DiagnosisSchema).nullable(),
  procedures: z.array(PopulatedProcedureSchema).nullable(),
  authorizationSubmissions: z.array(AuthorizationSubmissionSchema),
  orderSpecificQuestions: z.array(OrderSpecificQuestionSchema).nullable(),
});

export const SubmitPriorAuthorizationParsedConfigSchema =
  FlowSubmitPriorAuthorizationBlockConfigSchema.extend({
    priorAuthorizationInformation:
      FlowSubmitPriorAuthorizationBlockConfigSchema.shape.priorAuthorizationInformation.extend({
        copilotDataContext: FlowHumanTaskDocumentTranscriptionResponsesSchema,
        copilotDataContextObject: FlowHumanTaskCopilotDataContextObjectSchema.optional(),
        predictionOptions: z
          .object({
            preamble: z.string().optional(),
            outputRequirement: z.string().optional(),
            outro: z.string().optional(),
          })
          .optional(),
      }),
  });
export type SubmitPriorAuthorizationParsedConfig = z.infer<
  typeof SubmitPriorAuthorizationParsedConfigSchema
>;
